import { CstParser } from 'chevrotain';
import * as T from './openfoam.tokens.js';
import * as JT from './jinja.tokens.js';

class OpenFoamParser extends CstParser {
  constructor() {
    super(T.allTokens, { recoveryEnabled: true });
    const $ = this;

    $.RULE('file', () => {
      const statements = [];
      $.MANY(() => {
        const stmt = $.SUBRULE($.statement);
        if (stmt) statements.push(stmt);
      });
      return { type: 'file', statements };
    });

    $.RULE('statement', () => {
      console.log("Attempting to parse statement, next token:", $.LA(1).image);
      
      // Check if this looks like a list with nested parentheses (vector or connectivity list)
      const isNestedList = (() => {
        try {
          const LA1 = $.LA(1); // Name
          const LA2 = $.LA(2); // (
          const LA3 = $.LA(3); // (
          
          return LA1.tokenType === T.Name && 
                 LA2.tokenType === T.LParen && 
                 LA3.tokenType === T.LParen;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a string list
      const isStringList = (() => {
        try {
          // Basic pattern: Name ( followed by strings/names and ending with )
          const LA1 = $.LA(1); // Name
          const LA2 = $.LA(2); // (
          
          // First check the basic pattern start
          if (LA1.tokenType !== T.Name || LA2.tokenType !== T.LParen) {
            return false;
          }
          
          // Look ahead to check what follows the opening parenthesis
          const LA3 = $.LA(3);
          
          // If it's empty (just a closing parenthesis), it's still a valid string list
          if (LA3.tokenType === T.RParen) {
            return true;
          }
          
          // Check if the third token is a string literal or name (valid for string list)
          const isStringOrName = LA3.tokenType === T.StringLiteral || LA3.tokenType === T.Name;
          
          // If not a string or name, it's not a string list
          if (!isStringOrName) {
            return false;
          }
          
          // Now check what follows to distinguish from other patterns
          const LA4 = $.LA(4);
          
          // If followed by closing parenthesis, it's a simple string list with one item
          if (LA4.tokenType === T.RParen) {
            return true;
          }
          
          // If followed by another string/name or comma, it's a multi-item string list
          if (LA4.tokenType === T.StringLiteral || LA4.tokenType === T.Name || LA4.tokenType === T.Comma) {
            return true;
          }
          
          // Check for patterns that would indicate it's NOT a string list
          
          // If followed by opening brace, it's likely a dictionary list: name ( first {
          if (LA4.tokenType === T.LBrace) {
            return false;
          }
          
          // If followed by opening parenthesis, it's likely a nested list: name ( first (
          if (LA4.tokenType === T.LParen) {
            return false;
          }
          
          // Default to true if we've passed all the negative checks
          return true;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a dictionary list
      const isDictList = (() => {
        try {
          const LA1 = $.LA(1); // Name
          const LA2 = $.LA(2); // (
          const LA3 = $.LA(3); // Name
          const LA4 = $.LA(4); // {
          
          return LA1.tokenType === T.Name && 
                 LA2.tokenType === T.LParen && 
                 LA3.tokenType === T.Name &&
                 LA4.tokenType === T.LBrace;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a function object
      const isFunctionObject = (() => {
        try {
          const LA1 = $.LA(1); // Name
          const LA2 = $.LA(2); // {
          const LA3 = $.LA(3); // Name
          const LA4 = $.LA(4); // ;
          
          return LA1.tokenType === T.Name && 
                 LA2.tokenType === T.LBrace && 
                 LA3.tokenType === T.Name &&
                 LA4.tokenType === T.Semi;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a dictionary
      const isDictionary = (() => {
        try {
          const LA1 = $.LA(1); // Name or FoamFile
          const LA2 = $.LA(2); // {
          
          return (LA1.tokenType === T.Name || LA1.tokenType === T.FoamFile) && 
                 LA2.tokenType === T.LBrace;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a macro
      const isMacro = (() => {
        try {
          const tokenType = $.LA(1).tokenType;
          return tokenType === T.Include || 
                 tokenType === T.IncludeEtc || 
                 tokenType === T.IncludeFunc || 
                 tokenType === T.IncludeIfPresent || 
                 tokenType === T.Remove || 
                 tokenType === T.InputMode;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a codestream
      const isCodestream = (() => {
        try {
          return $.LA(1).tokenType === T.CodeStart;
        } catch (e) {
          return false;
        }
      })();
      
      // Check if this looks like a jinja node
      const isJinjaNode = (() => {
        try {
          const tokenType = $.LA(1).tokenType;
          return tokenType === JT.JinjaExprStart ||
                 tokenType === JT.JinjaIfStart ||
                 tokenType === JT.JinjaForStart ||
                 tokenType === JT.JinjaSetStart ||
                 tokenType === JT.JinjaBlockStart ||
                 tokenType === JT.JinjaExtendsStart ||
                 tokenType === JT.JinjaIncludeStart ||
                 tokenType === JT.JinjaMacroStart ||
                 tokenType === JT.JinjaCommentStart;
        } catch (e) {
          return false;
        }
      })();
      
      console.log("Statement type detection:", {
        isNestedList,
        isStringList,
        isDictList,
        isFunctionObject,
        isDictionary,
        isMacro,
        isCodestream,
        isJinjaNode
      });
      
      return $.OR([
        {
          GATE: () => isNestedList,
          ALT: () => $.SUBRULE($.vectorOrConnectivityList)
        },
        {
          GATE: () => isStringList,
          ALT: () => $.SUBRULE($.namedStringList)
        },
        {
          GATE: () => isDictList,
          ALT: () => $.SUBRULE($.namedDictList)
        },
        {
          GATE: () => isFunctionObject,
          ALT: () => $.SUBRULE($.functionObject)
        },
        {
          GATE: () => isDictionary,
          ALT: () => $.SUBRULE($.dictionary)
        },
        {
          GATE: () => isMacro,
          ALT: () => $.SUBRULE($.macro)
        },
        {
          GATE: () => isCodestream,
          ALT: () => $.SUBRULE($.codestream)
        },
        // Check for jinja nodes
        {
          GATE: () => isJinjaNode,
          ALT: () => $.SUBRULE($.jinjaNode)
        },
        // Check for substitution statements (e.g., $p;)
        {
          GATE: () => {
            try {
              return $.LA(1).tokenType === T.Substitution && $.LA(2).tokenType === T.Semi;
            } catch (e) {
              return false;
            }
          },
          ALT: () => $.SUBRULE($.substitutionStatement)
        },
        // Default to keyValue if none of the above patterns match
        { ALT: () => $.SUBRULE($.keyValue) },
      ]);
    });

    // Rule for Jinja nodes - now directly routes to specific statement types
    $.RULE('jinjaNode', () => {
      return $.OR([
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaExprStart,
          ALT: () => $.SUBRULE($.jinjaExpression)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaIfStart,
          ALT: () => $.SUBRULE($.jinjaIfStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaForStart,
          ALT: () => $.SUBRULE($.jinjaForStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaSetStart,
          ALT: () => $.SUBRULE($.jinjaSetStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaBlockStart,
          ALT: () => $.SUBRULE($.jinjaBlockStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaExtendsStart,
          ALT: () => $.SUBRULE($.jinjaExtendsStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaIncludeStart,
          ALT: () => $.SUBRULE($.jinjaIncludeStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaMacroStart,
          ALT: () => $.SUBRULE($.jinjaMacroStatement)
        },
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaCommentStart,
          ALT: () => $.SUBRULE($.jinjaComment)
        }
      ]);
    });

    // Rule for Jinja expressions (e.g., {{ variable }})
    $.RULE('jinjaExpression', () => {
      $.CONSUME(JT.JinjaExprStart);
      let content = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaExprChunk);
        content = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaExprEnd);
      const variableName = content.split(/\s+/)[0].trim();
      return { 
        type: 'jinja_expression', 
        content: content,
        variableName: variableName
      };
    });

    // Rule for Jinja if statements (including elif and else)
    $.RULE('jinjaIfStatement', () => {
      const branches = [];
      
      // Parse initial if block
      $.CONSUME(JT.JinjaIfStart);
      let ifCondition = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        ifCondition = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Parse body of if block
      const ifBody = $.SUBRULE($.jinjaBody);
      
      branches.push({
        type: 'if',
        condition: ifCondition,
        body: ifBody
      });
      
      // Parse optional elif blocks
      $.MANY1(() => {
        $.CONSUME(JT.JinjaElifStart);
        let elifCondition = '';
        $.OPTION2(() => {
          const chunk = $.CONSUME2(JT.JinjaStmtChunk);
          elifCondition = chunk.image.trim();
        });
        $.CONSUME2(JT.JinjaStmtEnd);
        const elifBody = $.SUBRULE2($.jinjaBody);
        branches.push({
          type: 'elif',
          condition: elifCondition,
          body: elifBody
        });
      });
      
      // Parse optional else block
      $.OPTION3(() => {
        $.CONSUME(JT.JinjaElseStart);
        $.OPTION4(() => {
          $.CONSUME3(JT.JinjaStmtChunk); // Usually empty for else, but consume if present
        });
        $.CONSUME3(JT.JinjaStmtEnd);
        const elseBody = $.SUBRULE3($.jinjaBody);
        branches.push({
          type: 'else',
          condition: null,
          body: elseBody
        });
      });
      
      // End of if statement
      $.CONSUME(JT.JinjaEndifStart);
      $.CONSUME4(JT.JinjaStmtEnd);
      
      return { type: 'jinja_if_statement', branches };
    });

    // Rule for Jinja for statements
    $.RULE('jinjaForStatement', () => {
      $.CONSUME(JT.JinjaForStart);
      let forContent = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        forContent = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Parse the for statement content to extract loop variable and iterable
      const parts = forContent.split(/\s+in\s+/);
      const loopVar = parts[0]?.trim() || '';
      const iterable = parts[1]?.trim() || '';
      
      const body = $.SUBRULE($.jinjaBody);
      
      $.CONSUME(JT.JinjaEndforStart);
      $.CONSUME2(JT.JinjaStmtEnd);
      
      return {
        type: 'jinja_for_statement',
        loopVar,
        iterable,
        body
      };
    });

    // Rule for Jinja set statements
    $.RULE('jinjaSetStatement', () => {
      $.CONSUME(JT.JinjaSetStart);
      let setContent = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        setContent = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Parse the set statement content to extract variable and value
      const parts = setContent.split(/\s*=\s*/);
      const variable = parts[0]?.trim() || '';
      const value = parts[1]?.trim() || null;
      
      return {
        type: 'jinja_set_statement',
        variable,
        value
      };
    });

    // Rule for Jinja block statements
    $.RULE('jinjaBlockStatement', () => {
      $.CONSUME(JT.JinjaBlockStart);
      let blockContent = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        blockContent = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Extract block name from content
      const blockName = blockContent || 'unnamed';
      
      const body = $.SUBRULE($.jinjaBody);
      
      $.CONSUME(JT.JinjaEndblockStart);
      $.OPTION2(() => {
        $.CONSUME2(JT.JinjaStmtChunk); // Optional block name in endblock
      });
      $.CONSUME2(JT.JinjaStmtEnd);
      
      return {
        type: 'jinja_block_statement',
        blockName,
        body
      };
    });

    // Rule for Jinja extends statements
    $.RULE('jinjaExtendsStatement', () => {
      $.CONSUME(JT.JinjaExtendsStart);
      let extendsContent = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        extendsContent = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Extract template name, removing quotes if present
      const template = extendsContent.replace(/^['"]|['"]$/g, '');
      
      return {
        type: 'jinja_extends_statement',
        template
      };
    });

    // Rule for Jinja include statements
    $.RULE('jinjaIncludeStatement', () => {
      $.CONSUME(JT.JinjaIncludeStart);
      let includeContent = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        includeContent = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Extract template name, removing quotes if present
      const template = includeContent.replace(/^['"]|['"]$/g, '');
      
      return {
        type: 'jinja_include_statement',
        template
      };
    });

    // Rule for Jinja macro statements
    $.RULE('jinjaMacroStatement', () => {
      $.CONSUME(JT.JinjaMacroStart);
      let macroContent = '';
      $.OPTION(() => {
        const chunk = $.CONSUME(JT.JinjaStmtChunk);
        macroContent = chunk.image.trim();
      });
      $.CONSUME(JT.JinjaStmtEnd);
      
      // Parse macro content to extract name and parameters
      const parts = macroContent.split(/\s*\(\s*/);
      const macroName = parts[0]?.trim() || 'unnamed';
      
      let parameters = [];
      if (parts[1]) {
        const paramString = parts[1].replace(/\s*\)\s*$/, '');
        if (paramString.trim()) {
          parameters = paramString.split(/\s*,\s*/).map(p => p.trim()).filter(p => p);
        }
      }
      
      const body = $.SUBRULE($.jinjaBody);
      
      $.CONSUME(JT.JinjaEndmacroStart);
      $.CONSUME2(JT.JinjaStmtEnd);
      
      return {
        type: 'jinja_macro_statement',
        macroName,
        parameters,
        body
      };
    });

    // Rule for Jinja comments
    $.RULE('jinjaComment', () => {
      $.CONSUME(JT.JinjaCommentStart);
      let content = '';
      $.OPTION(() => {
        content = $.CONSUME(JT.JinjaCommentChunk).image;
      });
      $.CONSUME(JT.JinjaCommentEnd);
      return { type: 'jinja_comment', value: content };
    });

    // Rule for substitution statements (e.g., $p;)
    $.RULE('substitutionStatement', () => {
      const substitution = $.CONSUME(T.Substitution).image;
      $.CONSUME(T.Semi);
      
      // Extract the variable name by removing the $ prefix
      const variableName = substitution.replace(/^\$/, '');
      
      return { 
        type: 'substitution', 
        value: substitution,
        variableName: variableName
      };
    });

    // Rule for the body of a Jinja statement (can contain OpenFOAM statements or other Jinja nodes)
    $.RULE('jinjaBody', () => {
      const statements = [];
      $.MANY(() => {
        const stmt = $.SUBRULE($.statement);
        if (stmt) statements.push(stmt);
      });
      return statements;
    });

    $.RULE('dictionary', () => {
      const name = $.OR([
        { ALT: () => $.CONSUME(T.FoamFile).image },
        { ALT: () => $.CONSUME(T.Name).image }
      ]);
      $.CONSUME(T.LBrace);
      const entries = [];
      $.MANY(() => {
        const entry = $.SUBRULE($.statement);
        if (entry) entries.push(entry);
      });
      $.CONSUME(T.RBrace);
      return { type: 'dictionary', name, entries };
    });

    $.RULE('keyValue', () => {
      const key = $.OR([
        { ALT: () => $.CONSUME(T.Name).image },
        ...Object.values(T.ReservedKeywords).map(tok => ({ ALT: () => $.CONSUME(tok).image }))
      ]);
      const value = $.SUBRULE($.value);
      $.CONSUME(T.Semi);
      return { type: 'keyValue', key, value };
    });

    $.RULE('value', () => {
      return $.OR([
        {
          GATE: () => $.LA(1).tokenType === JT.JinjaExprStart,
          ALT: () => {
            const jinjaExpr = $.SUBRULE($.jinjaExpression);
            return { 
              type: 'jinja_value', 
              expression: jinjaExpr.content,
              variableName: jinjaExpr.variableName
            };
          }
        },
        {
          GATE: () => $.LA(1).tokenType === T.Uniform || $.LA(1).tokenType === T.NonUniform,
          ALT: () => $.SUBRULE($.fieldvalue)
        },
        {
          // Add support for vector3 as a value
          GATE: () => {
            try {
              // Check for vector3 pattern: LParen Number Number Number RParen
              if ($.LA(1).tokenType !== T.LParen) return false;
              
              // Check if we have 3 numbers followed by RParen
              const isNumber2 = ($.LA(2).tokenType === T.IntegerLiteral || $.LA(2).tokenType === T.FloatLiteral);
              const isNumber3 = ($.LA(3).tokenType === T.IntegerLiteral || $.LA(3).tokenType === T.FloatLiteral);
              const isNumber4 = ($.LA(4).tokenType === T.IntegerLiteral || $.LA(4).tokenType === T.FloatLiteral);
              const hasRParen = ($.LA(5).tokenType === T.RParen);
              
              return isNumber2 && isNumber3 && isNumber4 && hasRParen;
            } catch (e) {
              return false;
            }
          },
          ALT: () => $.SUBRULE($.vector3)
        },
        {
          // Add support for connectivity4 as a value
          GATE: () => {
            try {
              // Check for connectivity4 pattern: LParen Number Number Number Number RParen
              if ($.LA(1).tokenType !== T.LParen) return false;
              
              // Check if we have 4 numbers followed by RParen
              const isNumber2 = ($.LA(2).tokenType === T.IntegerLiteral || $.LA(2).tokenType === T.FloatLiteral);
              const isNumber3 = ($.LA(3).tokenType === T.IntegerLiteral || $.LA(3).tokenType === T.FloatLiteral);
              const isNumber4 = ($.LA(4).tokenType === T.IntegerLiteral || $.LA(4).tokenType === T.FloatLiteral);
              const isNumber5 = ($.LA(5).tokenType === T.IntegerLiteral || $.LA(5).tokenType === T.FloatLiteral);
              const hasRParen = $.LA(6).tokenType === T.RParen;
              
              return isNumber2 && isNumber3 && isNumber4 && isNumber5 && hasRParen;
            } catch (e) {
              return false;
            }
          },
          ALT: () => $.SUBRULE($.connectivity4)
        },
        {
          GATE: () => $.LA(1).tokenType === T.IntegerLiteral || $.LA(1).tokenType === T.FloatLiteral,
          ALT: () => {
            const token = $.OR1([
              { ALT: () => $.CONSUME(T.IntegerLiteral) },
              { ALT: () => $.CONSUME(T.FloatLiteral) }
            ]);
            return Number(token.image);
          }
        },
        {
          GATE: () => $.LA(1).tokenType === T.StringLiteral,
          ALT: () => {
            const token = $.CONSUME(T.StringLiteral);
            return token.image.replace(/^"|"$/g, '');
          }
        },
        {
          GATE: () => $.LA(1).tokenType === T.Substitution,
          ALT: () => $.CONSUME(T.Substitution).image
        },
        {
          GATE: () => {
            try {
              const LA1 = $.LA(1);
              const LA2 = $.LA(2);
              
              // Allow values that start with Name, StringLiteral, IntegerLiteral, or FloatLiteral
              // But exclude patterns that should be handled by other rules
              const isValidStartToken = LA1.tokenType === T.Name || 
                                       LA1.tokenType === T.StringLiteral || 
                                       LA1.tokenType === T.IntegerLiteral || 
                                       LA1.tokenType === T.FloatLiteral;
              
              // Exclude patterns handled by other rules
              const isExcluded = (LA1.tokenType === T.Name && LA2.tokenType === T.LBrace); // dictionary
              
              return isValidStartToken && !isExcluded;
            } catch (e) {
              return false;
            }
          },
          ALT: () => {
            const values = [];
            $.AT_LEAST_ONE(() => {
              const value = $.OR2([
                {
                  // Handle function-like patterns: name(args)
                  GATE: () => {
                    try {
                      return $.LA(1).tokenType === T.Name && $.LA(2).tokenType === T.LParen;
                    } catch (e) {
                      return false;
                    }
                  },
                  ALT: () => {
                    const functionName = $.CONSUME(T.Name).image;
                    $.CONSUME(T.LParen);
                    const args = [];
                    
                    // Parse comma-separated arguments
                    $.OPTION(() => {
                      args.push($.OR3([
                        { ALT: () => $.CONSUME1(T.Name).image },
                        { ALT: () => $.CONSUME1(T.StringLiteral).image.replace(/^"|"$/g, '') },
                        { ALT: () => Number($.CONSUME1(T.IntegerLiteral).image) },
                        { ALT: () => Number($.CONSUME1(T.FloatLiteral).image) }
                      ]));
                      $.MANY(() => {
                        $.CONSUME(T.Comma);
                        args.push($.OR4([
                          { ALT: () => $.CONSUME2(T.Name).image },
                          { ALT: () => $.CONSUME2(T.StringLiteral).image.replace(/^"|"$/g, '') },
                          { ALT: () => Number($.CONSUME2(T.IntegerLiteral).image) },
                          { ALT: () => Number($.CONSUME2(T.FloatLiteral).image) }
                        ]));
                      });
                    });
                    
                    $.CONSUME(T.RParen);
                    return { type: 'functionCall', name: functionName, arguments: args };
                  }
                },
                {
                  GATE: () => $.LA(1).tokenType === T.Name,
                  ALT: () => $.CONSUME3(T.Name).image
                },
                {
                  GATE: () => $.LA(1).tokenType === T.IntegerLiteral,
                  ALT: () => Number($.CONSUME3(T.IntegerLiteral).image)
                },
                {
                  GATE: () => $.LA(1).tokenType === T.FloatLiteral,
                  ALT: () => Number($.CONSUME3(T.FloatLiteral).image)
                },
                {
                  GATE: () => $.LA(1).tokenType === T.StringLiteral,
                  ALT: () => $.CONSUME3(T.StringLiteral).image.replace(/^"|"$/g, '')
                }
              ]);
              values.push(value);
            });
            return values.length === 1 ? values[0] : { type: 'valueList', values };
          }
        },
        {
          GATE: () => $.LA(1).tokenType === T.LBracket,
          ALT: () => $.SUBRULE($.symbolicDimension)
        },
      ]);
    });

    $.RULE('fieldvalue', () => {
      return $.OR5([
        {
          GATE: () => $.LA(1).tokenType === T.Uniform,
          ALT: () => {
            $.CONSUME(T.Uniform);
            const value = $.SUBRULE($.value);
            return { type: 'uniform', value };
          }
        },
        {
          GATE: () => $.LA(1).tokenType === T.NonUniform,
          ALT: () => {
            $.CONSUME(T.NonUniform);
            let typeName = null;
            $.OPTION1(() => { typeName = $.CONSUME(T.Name).image; });
            
            // For non-uniform values, we can have either a string list or a dictionary list
            const value = $.OR6([
              { 
                GATE: () => $.LA(1).tokenType === T.StringLiteral,
                ALT: () => {
                  const strings = [];
                  $.AT_LEAST_ONE1(() => {
                    const value = $.CONSUME(T.StringLiteral).image.replace(/^"|"$/g, '');
                    strings.push(value);
                    $.OPTION2(() => {
                      $.CONSUME1(T.Comma);
                    });
                  });
                  return { type: 'stringList', values: strings };
                }
              },
              { 
                GATE: () => $.LA(1).tokenType === T.LBrace,
                ALT: () => {
                  const dictionaries = [];
                  $.AT_LEAST_ONE2(() => {
                    $.CONSUME(T.LBrace);
                    const entries = [];
                    $.MANY(() => {
                      const entry = $.SUBRULE($.statement);
                      if (entry) entries.push(entry);
                    });
                    $.CONSUME(T.RBrace);
                    dictionaries.push({ type: 'dictionary', entries });
                    $.OPTION3(() => {
                      $.CONSUME2(T.Comma);
                    });
                  });
                  return { type: 'dictionaryList', values: dictionaries };
                }
              },
              {
                GATE: () => $.LA(1).tokenType === T.BinaryBlob,
                ALT: () => {
                  const blob = $.CONSUME(T.BinaryBlob).image;
                  return { type: 'binaryBlob', value: blob };
                }
              }
            ]);
            
            return { type: 'nonuniform', typeName, value };
          }
        }
      ]);
    });

    $.RULE('symbolicDimension', () => {
      $.CONSUME(T.LBracket);
      const values = [];
      $.MANY(() => {
        const value = $.OR([
          { ALT: () => $.CONSUME(T.Name).image },
          { ALT: () => $.CONSUME(T.IntegerLiteral).image },
          { ALT: () => $.CONSUME(T.FloatLiteral).image },
          { ALT: () => $.CONSUME(T.Caret).image },
          { ALT: () => $.CONSUME(T.Mul).image },
          { ALT: () => $.CONSUME(T.Div).image }
        ]);
        values.push(value);
      });
      $.CONSUME(T.RBracket);
      return { type: 'dimension', values };
    });

    $.RULE('blockSection', () => {
      const name = $.CONSUME(T.Name).image;
      $.CONSUME(T.LParen);
      const entries = [];
      $.MANY(() => entries.push($.SUBRULE($.blockEntry)));
      $.CONSUME(T.RParen);
      $.CONSUME(T.Semi);
      return { type: 'blockSection', name, entries };
    });

    $.RULE('blockEntry', () => {
      return $.OR([
        { 
          GATE: () => $.LA(1).tokenType === T.Name,
          ALT: () => $.CONSUME(T.Name).image 
        },
        { 
          GATE: () => $.LA(1).tokenType === T.IntegerLiteral,
          ALT: () => Number($.CONSUME(T.IntegerLiteral).image) 
        },
        { 
          GATE: () => $.LA(1).tokenType === T.FloatLiteral,
          ALT: () => Number($.CONSUME(T.FloatLiteral).image) 
        },
        { 
          GATE: () => $.LA(1).tokenType === T.StringLiteral,
          ALT: () => $.CONSUME(T.StringLiteral).image.replace(/^"|"$/g, '') 
        }
      ]);
    });

    $.RULE('tuple', () => {
      const name = $.CONSUME(T.Name).image;
      $.CONSUME(T.LParen);
      const items = [];
      $.OPTION(() => {
        items.push($.SUBRULE($.tupleItem));
        $.MANY(() => {
          $.CONSUME(T.Comma);
          items.push($.SUBRULE2($.tupleItem));
        });
      });
      $.CONSUME(T.RParen);
      $.CONSUME(T.Semi);
      return { type: 'tuple', name, items };
    });

    $.RULE('tupleItem', () => {
      return $.OR([
        { 
          GATE: () => $.LA(1).tokenType === T.IntegerLiteral,
          ALT: () => Number($.CONSUME(T.IntegerLiteral).image) 
        },
        { 
          GATE: () => $.LA(1).tokenType === T.FloatLiteral,
          ALT: () => Number($.CONSUME(T.FloatLiteral).image) 
        },
        { 
          GATE: () => $.LA(1).tokenType === T.StringLiteral,
          ALT: () => $.CONSUME(T.StringLiteral).image.replace(/^"|"$/g, '') 
        },
        { 
          GATE: () => $.LA(1).tokenType === T.Name,
          ALT: () => $.CONSUME(T.Name).image 
        }
      ]);
    });

    $.RULE('macro', () => {
      const directive = $.OR([
        { ALT: () => $.CONSUME(T.Include).image },
        { ALT: () => $.CONSUME(T.IncludeEtc).image },
        { ALT: () => $.CONSUME(T.IncludeFunc).image },
        { ALT: () => $.CONSUME(T.IncludeIfPresent).image },
        { ALT: () => $.CONSUME(T.Remove).image },
        { ALT: () => $.CONSUME(T.InputMode).image }
      ]);
      
      // Try to parse quoted include string first, fallback to rest of line
      const argument = $.OR1([
        { ALT: () => {
          const includeString = $.CONSUME(T.IncludeString).image;
          return includeString;
        }},
        { ALT: () => $.CONSUME(T.RestOfLine).image.trim() }
      ]);
      
      return { type: 'macro', directive, argument };
    });

    $.RULE('codestream', () => {
      $.CONSUME(T.CodeStart);
      let content = '';
      $.OPTION(() => {
        content = $.CONSUME(T.CodeStreamChunk).image;
      });
      $.CONSUME(T.CodeEnd);
      return { type: 'codestream', content };
    });

    $.RULE('functionObject', () => {
      const name = $.CONSUME(T.Name).image;
      $.CONSUME(T.LBrace);
      const type = $.CONSUME2(T.Name).image;
      $.CONSUME(T.Semi);
      const entries = [];
      $.MANY(() => {
        const entry = $.SUBRULE($.statement);
        if (entry) entries.push(entry);
      });
      $.CONSUME(T.RBrace);
      return { 
        type: 'functionObject', 
        name, 
        functionType: type,
        entries 
      };
    });

    // Rule for connectivity4 (e.g., (0 1 2 3))
    $.RULE('connectivity4', () => {
      console.log("Attempting to parse connectivity4");
      $.CONSUME(T.LParen);
      console.log("Found opening parenthesis");
      const indices = [];
      
      // First index
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        indices.push(Number($.CONSUME(T.IntegerLiteral).image));
      } else {
        indices.push(Number($.CONSUME(T.FloatLiteral).image));
      }
      console.log("Parsed first index:", indices[0]);
      
      // Second index
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        indices.push(Number($.CONSUME1(T.IntegerLiteral).image));
      } else {
        indices.push(Number($.CONSUME1(T.FloatLiteral).image));
      }
      console.log("Parsed second index:", indices[1]);
      
      // Third index
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        indices.push(Number($.CONSUME2(T.IntegerLiteral).image));
      } else {
        indices.push(Number($.CONSUME2(T.FloatLiteral).image));
      }
      console.log("Parsed third index:", indices[2]);
      
      // Fourth index
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        indices.push(Number($.CONSUME3(T.IntegerLiteral).image));
      } else {
        indices.push(Number($.CONSUME3(T.FloatLiteral).image));
      }
      console.log("Parsed fourth index:", indices[3]);
      
      // Check if there are more numbers (should be exactly 4)
      if ($.LA(1).tokenType === T.IntegerLiteral || $.LA(1).tokenType === T.FloatLiteral) {
        console.log("Error: Too many integers in connectivity4, next token:", $.LA(1));
        this.ERROR("Expected exactly 4 integers in connectivity4");
      }
      
      $.CONSUME(T.RParen);
      console.log("Found closing parenthesis, connectivity4 parsed successfully");
      
      return { type: 'connectivity4', indices };
    });

    // Rule for connectivity4list (e.g., faces ((0 1 2 3) (4 5 6 7));)
    $.RULE('connectivity4list', () => {
      console.log("Attempting to parse connectivity4list");
      const name = $.CONSUME(T.Name).image;
      console.log("Connectivity4list name:", name);
      $.CONSUME(T.LParen);
      const connections = [];
      try {
        $.AT_LEAST_ONE(() => {
          console.log("Parsing connection in connectivity4list");
          connections.push($.SUBRULE2($.connectivity4));
        });
        console.log(`Parsed ${connections.length} connections successfully`);
        $.CONSUME(T.RParen);
        $.CONSUME(T.Semi);
      } catch (error) {
        console.error("Error in connectivity4list:", error.message);
        throw error;
      }
      return { type: 'connectivity4list', name, connections };
    });

    // New rule for vector3 (e.g., (1 2 3))
    $.RULE('vector3', () => {
      console.log("Parsing vector3");
      $.CONSUME(T.LParen);
      
      const components = [];
      
      // First component
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        components.push(Number($.CONSUME(T.IntegerLiteral).image));
      } else {
        components.push(Number($.CONSUME(T.FloatLiteral).image));
      }
      
      // Second component
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        components.push(Number($.CONSUME1(T.IntegerLiteral).image));
      } else {
        components.push(Number($.CONSUME1(T.FloatLiteral).image));
      }
      
      // Third component
      if ($.LA(1).tokenType === T.IntegerLiteral) {
        components.push(Number($.CONSUME2(T.IntegerLiteral).image));
      } else {
        components.push(Number($.CONSUME2(T.FloatLiteral).image));
      }
      
      $.CONSUME(T.RParen);
      
      return { type: 'vector3', components };
    });

    // New rule for vector3list (e.g., vertices ((1 2 3) (4 5 6));)
    $.RULE('vector3list', () => {
      console.log("Attempting to parse vector3list");
      const name = $.CONSUME(T.Name).image;
      console.log("Vector3list name:", name);
      $.CONSUME(T.LParen);
      const vectors = [];
      try {
        $.AT_LEAST_ONE(() => {
          console.log("Parsing vector in vector3list");
          vectors.push($.SUBRULE($.vector3));
        });
        console.log(`Parsed ${vectors.length} vectors successfully`);
        $.CONSUME(T.RParen);
        $.CONSUME(T.Semi);
      } catch (error) {
        console.error("Error in vector3list:", error.message);
        throw error;
      }
      return { type: 'vector3list', name, vectors };
    });

    // New rule for vectorOrConnectivityList
    $.RULE('vectorOrConnectivityList', () => {
      console.log("Attempting to parse vectorOrConnectivityList");
      
      // Look ahead to determine if this is a vector3list or connectivity4list
      const isConnectivity4 = (() => {
        try {
          // We need to look ahead several tokens to determine the pattern
          // Name ( ( num num num num ) ... ) ;  <- connectivity4list
          // Name ( ( num num num ) ... ) ;      <- vector3list
          
          // Skip to the first number in the first inner list
          const LA1 = $.LA(1); // Name
          const LA2 = $.LA(2); // (
          const LA3 = $.LA(3); // (
          const LA4 = $.LA(4); // First number
          const LA5 = $.LA(5); // Second number
          const LA6 = $.LA(6); // Third number
          const LA7 = $.LA(7); // Fourth number or )
          
          console.log("Connectivity4List GATE check:", {
            result: LA7.tokenType !== T.RParen,
            tokens: [LA1, LA2, LA3, LA4, LA5, LA6, LA7],
            types: [LA1.tokenType, LA2.tokenType, LA3.tokenType, LA4.tokenType, LA5.tokenType, LA6.tokenType, LA7.tokenType]
          });
          
          // If the 7th token is not a closing parenthesis, it must be a fourth number
          return LA7.tokenType !== T.RParen;
        } catch (e) {
          return false;
        }
      })();
      
      return $.OR([
        {
          GATE: () => isConnectivity4,
          ALT: () => $.SUBRULE($.connectivity4list)
        },
        {
          GATE: () => !isConnectivity4,
          ALT: () => $.SUBRULE($.vector3list)
        }
      ]);
    });

    $.RULE('namedStringList', () => {
      console.log("Parsing namedStringList");
      const name = $.CONSUME(T.Name).image;
      console.log(`namedStringList name: ${name}`);

      $.CONSUME(T.LParen);
      const strings = [];

      $.MANY(() => {
        console.log("Parsing string in namedStringList");
        
        // Handle both quoted strings and unquoted names as strings
        const value = $.OR([
          { 
            ALT: () => {
              const stringLiteral = $.CONSUME(T.StringLiteral).image;
              return stringLiteral.replace(/^"|"$/g, ''); // Remove quotes
            }
          },
          { 
            ALT: () => $.CONSUME1(T.Name).image // Use name directly as string
          }
        ]);
        
        strings.push(value);
        
        // Optional comma between strings
        $.OPTION(() => {
          $.CONSUME(T.Comma);
        });
      });

      $.CONSUME(T.RParen);
      $.CONSUME(T.Semi);

      return {
        type: 'namedStringList',
        name,
        strings
      };
    });

    $.RULE('namedDictList', () => {
      console.log("Parsing namedDictList");
      const name = $.CONSUME(T.Name).image;
      console.log(`namedDictList name: ${name}`);

      $.CONSUME(T.LParen);
      const dictionaries = [];

      $.MANY(() => {
        console.log("Parsing dictionary in namedDictList");
        // Consume the dictionary directly using the dictionary rule
        const dict = $.SUBRULE($.dictionary);
        dictionaries.push(dict);
        
        // Optional comma between dictionaries
        $.OPTION(() => {
          $.CONSUME(T.Comma);
        });
      });

      $.CONSUME(T.RParen);
      $.CONSUME(T.Semi);

      return {
        type: 'namedDictList',
        name,
        dictionaries
      };
    });

    this.performSelfAnalysis();
  }
}

export const parserInstance = new OpenFoamParser();






